import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
    InputNumber,
} from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';

const { TextArea } = Input;
const { Option } = Select;



interface PfModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: {
        pf_id?: number;
        pf_name?: string;
        pf_margin?: number;
    } | null;
    isEditMode?: boolean;
}

const PfModal: React.FC<PfModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);

    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };


    // Handle form submit
    const handleAddSc = async (values: any) => {
        try {
            const dataVal = {
                pf_name: values.pf_name,
                pf_margin: values.pf_margin,
                ...(initialValues?.pf_id && { pf_id: initialValues.pf_id }),
            };
            const url = isEditMode
                ? 'edit-price-formula'
                : 'add-price-formula';

            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });
            console.log(dataVal);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditMode ? 'Price Formula updated successfully!' : 'Price Formula created successfully!',
            );
            form.resetFields();

            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} Price Formula`);
        }
    };

    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                pf_name: initialValues.pf_name,
                pf_margin: initialValues.pf_margin,
            });
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditMode, initialValues, form]);




    return (
        <Modal
            title={isEditMode ? 'Edit Price Formula' : 'Add New Price Formula'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddSc}
                autoComplete="off"
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="pf_name"
                            className='px-8'
                            label={
                                <span>
                                    Price Formula &nbsp;
                                    <span style={{ fontSize: '12px', color: '#888' }}>
                                        (Max 255 characters)
                                    </span>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Please enter the Price Formula Name' },
                                { max: 255, message: 'Maximum 255 characters allowed' },
                            ]}
                        >
                            <Input
                                showCount
                                maxLength={255}
                                placeholder="e.g., Price Formula "

                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>

                    <Form.Item
                        name="pf_margin"
                        className="px-8"
                        label={
                            <span>
                            Margin (%) &nbsp;
                            <span style={{ fontSize: '12px', color: '#888' }}>
                                (0.00 – 999.99)
                            </span>
                            </span>
                        }
                        rules={[
                            { required: true, message: 'Please enter the margin percentage' },
                            {
                            type: 'number',
                            min: 0,
                            max: 999.99,
                            message: 'Margin must be between 0 and 999.99',
                            },
                        ]}
                        >
                        <InputNumber
                            style={{ width: '100%' }}
                            placeholder="e.g., 15.50"
                            className="py-7 input-bordor-radius"
                            min={0}
                            max={999.99}
                            step={0.01}
                            precision={2}
                        />
                        </Form.Item>
                    </Col>
                </Row>


                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(false)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit & Create New Price Formula
                        </Button>}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default PfModal;